import React, { useState } from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Sparkles, RefreshCw, Copy, FileDown, Target, Users, Megaphone, Gauge,
  Layers, TrendingUp, AlertTriangle, Lightbulb, ListChecks, Link2, Quote,
  Mail, Phone, Linkedin, Search, CheckCircle2, Globe, MapPin, Brain, Loader2,
} from "lucide-react";
import { useTheme } from "@/components/ThemeProvider";

// Section accent hex → lightened equivalent for dark mode (inline colors can't
// be overridden by CSS, so we swap them here based on the active theme).
const DARK_ACCENT = {
  "#4e3c97": "#a99ce0", "#db2777": "#f472b6", "#0891b2": "#38bdf8",
  "#059669": "#34d399", "#e11d48": "#fb7185", "#ea580c": "#fb923c",
  "#ca8a04": "#fbbf24", "#0d9488": "#2dd4bf",
};

const DOSSIER_STAGES = [
  { key: "crawling", label: "Crawling website", icon: Globe },
  { key: "gbp", label: "Checking Google Business Profile", icon: MapPin },
  { key: "researching", label: "Generating AI dossier", icon: Brain },
  { key: "scoring", label: "Scoring & finalizing", icon: Gauge },
];

const AnalyzeProgress = ({ stage }) => {
  const order = ["queued", "crawling", "gbp", "researching", "scoring", "completed"];
  const current = order.indexOf(stage || "queued");
  return (
    <div className="w-full max-w-sm mt-2 space-y-2" data-testid="dossier-progress">
      {DOSSIER_STAGES.map((s) => {
        const idx = order.indexOf(s.key);
        const done = current > idx;
        const active = current === idx;
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-center gap-2.5 text-left" data-testid={`dossier-stage-${s.key}`}>
            {done ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
            ) : active ? (
              <Loader2 className="w-4 h-4 text-indigo-500 animate-spin shrink-0" />
            ) : (
              <Icon className="w-4 h-4 text-slate-300 shrink-0" />
            )}
            <span className={`text-[11px] ${done ? "text-emerald-700" : active ? "text-indigo-700 font-semibold" : "text-slate-400"}`}>
              {s.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const Section = ({ icon: Icon, title, children, color = "#4e3c97" }) => {
  const { resolved } = useTheme();
  const c = resolved === "dark" ? (DARK_ACCENT[color] || color) : color;
  return (
    <div className="mb-5" data-testid={`dossier-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: c }} />
        <h3 className="text-[11px] font-bold uppercase tracking-wider" style={{ color: c }}>{title}</h3>
      </div>
      {children}
    </div>
  );
};

const toArr = (v) =>
  Array.isArray(v) ? v
    : v == null || v === "" ? []
    : typeof v === "object" ? Object.values(v)
    : [String(v)];

const Chips = ({ items, cls = "bg-indigo-50 text-indigo-700 border-indigo-100" }) => {
  const arr = toArr(items);
  return arr.length ? (
    <div className="flex flex-wrap gap-1.5">
      {arr.map((t, i) => <span key={i} className={`text-[10px] px-2 py-0.5 rounded border ${cls}`}>{typeof t === "object" ? JSON.stringify(t) : t}</span>)}
    </div>
  ) : <p className="text-[11px] text-muted-foreground italic">None detected.</p>;
};

const Bullets = ({ items }) => {
  const arr = toArr(items);
  return arr.length ? (
    <ul className="space-y-1">
      {arr.map((t, i) => <li key={i} className="text-[12px] text-slate-700 flex gap-1.5"><span className="text-slate-300 mt-0.5">•</span><span>{typeof t === "object" ? JSON.stringify(t) : t}</span></li>)}
    </ul>
  ) : <p className="text-[11px] text-muted-foreground italic">None detected.</p>;
};

const confColor = (c) => ({
  high: "bg-emerald-100 text-emerald-700 border-emerald-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-rose-100 text-rose-700 border-rose-200",
}[(c || "low").toLowerCase()] || "bg-slate-100 text-slate-600");

export default function ProspectDossier({ company, API, onAnalyze, analyzing }) {
  const [busy, setBusy] = useState("");
  const d = company?.dossier;
  const status = company?.dossier_status;

  const copyMarkdown = async () => {
    setBusy("copy");
    try {
      const res = await fetch(`${API}/companies/${company.company_id}/dossier/markdown`, { credentials: "include" });
      const data = await res.json();
      await navigator.clipboard.writeText(data.markdown);
      toast.success("Dossier copied to clipboard");
    } catch { toast.error("Copy failed"); }
    finally { setBusy(""); }
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await fetch(`${API}/companies/${company.company_id}/dossier/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Dossier_${(company.name || "prospect").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Dossier PDF downloaded");
    } catch { toast.error("PDF generation failed"); }
    finally { setBusy(""); }
  };

  // Empty / loading states
  if (status === "running" || analyzing) {
    return (
      <Card className="p-8 border-indigo-100 bg-gradient-to-br from-indigo-50/60 to-white" data-testid="dossier-loading">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="relative"><Sparkles className="w-7 h-7 text-indigo-500" /><RefreshCw className="w-4 h-4 text-indigo-400 animate-spin absolute -right-2 -top-1" /></div>
          <p className="text-sm font-semibold text-slate-800">Researching across multiple sources…</p>
          <AnalyzeProgress stage={company?.dossier_stage} />
          <p className="text-[11px] text-muted-foreground max-w-md mt-1">Crawling the website, detecting ad &amp; tech signals, finding contacts, and synthesizing a sales-ready dossier. ~1 minute.</p>
        </div>
      </Card>
    );
  }

  if (!d) {
    return (
      <Card className="p-8 border-dashed border-2 border-indigo-200 bg-indigo-50/30" data-testid="dossier-empty">
        <div className="flex flex-col items-center text-center gap-3">
          <Sparkles className="w-7 h-7 text-indigo-500" />
          <p className="text-sm font-semibold text-slate-800">Generate a Prospect Dossier</p>
          <p className="text-[11px] text-muted-foreground max-w-md">Multi-source deep research → executive summary, marketing &amp; ad gaps, decision-maker contacts with phone numbers, and tailored pitch angles. Meeting-ready in ~1 minute.</p>
          <Button data-testid="deep-analyze-cta" onClick={onAnalyze} className="bg-indigo-600 hover:bg-indigo-700 text-white" size="sm">
            <Sparkles className="w-4 h-4 mr-1.5" /> Deep Analyze
          </Button>
          {status === "failed" && (
            <p className="text-[10px] text-rose-500 max-w-md" data-testid="dossier-error-reason">
              {company?.dossier_error || "Last run failed — try again."}
            </p>
          )}
        </div>
      </Card>
    );
  }

  const ad = company?.ad_analysis;
  return (
    <Card className="overflow-hidden" data-testid="prospect-dossier">
      {/* Header */}
      <div className="px-5 py-4 text-white" style={{ background: "linear-gradient(135deg,#1f115a,#4e3c97)" }}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2">
              <Badge className="bg-white/20 text-white border-0 text-[8px] tracking-widest uppercase">AI Dossier</Badge>
              {ad?.confidence && <Badge className="bg-white/15 text-white border-0 text-[8px]">Ad signal: {ad.confidence}</Badge>}
            </div>
            <h2 className="text-lg font-bold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>Prospect Intelligence</h2>
          </div>
          <div className="flex gap-1.5">
            <Button data-testid="dossier-copy-btn" onClick={copyMarkdown} disabled={busy === "copy"} size="sm" variant="secondary" className="h-7 text-[10px] bg-white/15 hover:bg-white/25 text-white border-0">
              {busy === "copy" ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Copy className="w-3 h-3 mr-1" />} Copy
            </Button>
            <Button data-testid="dossier-pdf-btn" onClick={downloadPdf} disabled={busy === "pdf"} size="sm" variant="secondary" className="h-7 text-[10px] bg-white/15 hover:bg-white/25 text-white border-0">
              {busy === "pdf" ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <FileDown className="w-3 h-3 mr-1" />} PDF
            </Button>
            <Button data-testid="dossier-regen-btn" onClick={onAnalyze} disabled={analyzing} size="sm" variant="secondary" className="h-7 text-[10px] bg-white/15 hover:bg-white/25 text-white border-0">
              <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
            </Button>
          </div>
        </div>
      </div>

      <div className="p-5">
        {/* Executive summary */}
        <div className="bg-indigo-50/60 border-l-4 border-indigo-500 rounded p-3 mb-5">
          <p className="text-[12px] text-slate-800 leading-relaxed">{d.executive_summary}</p>
        </div>

        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
          <Section icon={Target} title="Core Offerings"><Chips items={d.core_offerings} /></Section>
          <Section icon={Users} title="Target Audience"><p className="text-[12px] text-slate-700">{d.target_audience}</p></Section>
        </div>

        <Section icon={Megaphone} title="Marketing & Content">
          <div className="space-y-1.5">
            <div><span className="text-[10px] font-semibold text-slate-500">Channels: </span><Chips items={d.marketing_channels} cls="bg-sky-50 text-sky-700 border-sky-100" /></div>
            <p className="text-[12px] text-slate-700"><span className="text-[10px] font-semibold text-slate-500">Strategy: </span>{d.content_strategy}</p>
            {toArr(d.content_themes).length > 0 && <p className="text-[11px] text-slate-600"><span className="text-[10px] font-semibold text-slate-500">Recent themes: </span>{toArr(d.content_themes).join(", ")}</p>}
          </div>
        </Section>

        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
          <Section icon={Megaphone} title="Advertising Observations" color="#db2777"><p className="text-[12px] text-slate-700">{d.ad_observations}</p></Section>
          <Section icon={Gauge} title="SEO / UX Observations" color="#0891b2"><p className="text-[12px] text-slate-700">{d.seo_ux_observations}</p></Section>
        </div>

        <Section icon={Layers} title="Tech & Marketing Stack"><Chips items={d.tech_stack} cls="bg-violet-50 text-violet-700 border-violet-100" /></Section>

        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
          <Section icon={TrendingUp} title="Strengths" color="#059669"><Bullets items={d.strengths} /></Section>
          <Section icon={AlertTriangle} title="Weaknesses & Gaps" color="#e11d48"><Bullets items={d.weaknesses} /></Section>
        </div>

        <Section icon={AlertTriangle} title="Pain Points" color="#ea580c"><Bullets items={d.pain_points} /></Section>

        {/* Pitch angles */}
        <Section icon={Lightbulb} title="Pitch Angles" color="#ca8a04">
          <div className="space-y-2">
            {toArr(d.pitch_angles).map((p, i) => (
              <div key={i} className="bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded p-2.5" data-testid={`pitch-angle-${i}`}>
                <p className="text-[12px] font-semibold text-slate-800">{typeof p === "object" ? p.angle : p}</p>
                <p className="text-[11px] text-slate-600 mt-0.5">{typeof p === "object" ? p.rationale : ""}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Contacts */}
        <Section icon={Users} title="Key Contacts">
          <div className="grid md:grid-cols-2 gap-2">
            {toArr(d.contacts).filter((c) => c && typeof c === "object").length === 0 && <p className="text-[11px] text-muted-foreground italic">No contacts discovered.</p>}
            {toArr(d.contacts).filter((c) => c && typeof c === "object").map((c, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3" data-testid={`dossier-contact-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[12px] font-bold text-slate-800">{c.name || "Contact"}</p>
                  <Badge variant="outline" className={`text-[8px] ${confColor(c.confidence)}`}>{(c.confidence || "low")} confidence</Badge>
                </div>
                <p className="text-[11px] text-slate-600">{c.title}</p>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px]">
                  {c.email && <a href={`mailto:${c.email}`} className="text-indigo-600 flex items-center gap-1"><Mail className="w-2.5 h-2.5" />{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone}`} className="text-indigo-600 flex items-center gap-1"><Phone className="w-2.5 h-2.5" />{c.phone}</a>}
                  {c.linkedin && <a href={c.linkedin.startsWith("http") ? c.linkedin : `https://${c.linkedin}`} target="_blank" rel="noopener noreferrer" className="text-indigo-600"><Linkedin className="w-3 h-3" /></a>}
                </div>
                {c.icebreaker && <p className="text-[10px] italic text-slate-500 mt-1.5 flex gap-1"><Quote className="w-2.5 h-2.5 shrink-0 mt-0.5" />{c.icebreaker}</p>}
              </div>
            ))}
          </div>
        </Section>

        <div className="grid md:grid-cols-2 gap-x-8 gap-y-1">
          <Section icon={Search} title="Recent Updates" color="#0d9488"><Bullets items={d.recent_updates} /></Section>
          <Section icon={ListChecks} title="Next Steps" color="#4e3c97"><Bullets items={d.next_steps} /></Section>
        </div>

        {toArr(d.sources).length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <p className="text-[9px] text-muted-foreground flex items-center gap-1 flex-wrap">
              <Link2 className="w-2.5 h-2.5" /> Sources:
              {toArr(d.sources).slice(0, 6).map((s, i) => <span key={i} className="text-slate-400">{s}{i < Math.min(toArr(d.sources).length, 6) - 1 ? "," : ""}</span>)}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
